import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { Card, Button, Input, PageHeader, Badge } from "@/components/kit";
import { FloppyDisk, Info, Bank } from "@phosphor-icons/react";

const FIELD_LABEL = {
  name: "Full name",
  phone: "Phone",
  email: "Email",
  address: "Address",
  id_type: "ID type",
  id_number: "ID number",
  agent_id: "Assigned agent",
  notes: "Notes",
  nominees: "Nominees",
};

export default function SettingsPage() {
  const { user } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [fields, setFields] = useState([]);
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [upi, setUpi] = useState({ vpa: "", payee_name: "", merchant_code: "" });
  const [upiSaving, setUpiSaving] = useState(false);
  const [upiMsg, setUpiMsg] = useState("");

  const isSuperAdmin = user.role === "super_admin";

  const load = async () => {
    try {
      const [pol, upiRes] = await Promise.all([
        api.get("/settings/edit-policy"),
        api.get("/settings/upi").catch(() => ({ data: {} })),
      ]);
      setPolicy(pol.data.policy || {});
      setFields(pol.data.fields || []);
      setRoles(pol.data.roles || []);
      setUpi({
        vpa: upiRes.data.vpa || "",
        payee_name: upiRes.data.payee_name || "",
        merchant_code: upiRes.data.merchant_code || "",
      });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Failed to load policy");
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (field, role) => {
    setPolicy((prev) => {
      const cur = new Set(prev[field] || []);
      if (cur.has(role)) cur.delete(role);
      else cur.add(role);
      return { ...prev, [field]: Array.from(cur) };
    });
  };

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      await api.put("/settings/edit-policy", { policy });
      setMsg("Policy saved. New edits will follow the new rules immediately.");
      setTimeout(() => setMsg(""), 4000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveUpi = async () => {
    setUpiSaving(true); setUpiMsg("");
    try {
      await api.put("/settings/upi", upi);
      setUpiMsg("Society UPI saved. Agents can now generate QR codes for collections.");
      setTimeout(() => setUpiMsg(""), 4000);
    } catch (e) {
      setUpiMsg(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setUpiSaving(false);
    }
  };

  if (!policy) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <PageHeader
        title="Settings"
        subtitle="Society UPI details, security, and customer-edit approval policy."
      />

      <ChangePasswordCard />

      <Card className="p-5 mb-6" data-testid="upi-settings-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <Bank size={20} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="heading font-semibold">Society bank UPI</div>
            <div className="text-xs text-slate-500 mb-3">
              This VPA is used to generate the QR code an agent shows to a customer during field collection. All UPI apps (PhonePe, GPay, Paytm, BHIM…) can scan it.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="UPI ID (VPA)"
                placeholder="society@okicici"
                value={upi.vpa}
                onChange={(e) => setUpi({ ...upi, vpa: e.target.value })}
                testId="upi-vpa"
                disabled={!isSuperAdmin}
              />
              <Input
                label="Payee display name"
                placeholder="Ujjwal Multipurpose Cooperative Society Ltd"
                value={upi.payee_name}
                onChange={(e) => setUpi({ ...upi, payee_name: e.target.value })}
                testId="upi-payee"
                disabled={!isSuperAdmin}
              />
            </div>
            {upiMsg && <div className="mt-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="upi-saved-msg">{upiMsg}</div>}
            {isSuperAdmin && (
              <div className="mt-3">
                <Button onClick={saveUpi} disabled={upiSaving} data-testid="save-upi-btn">
                  <FloppyDisk size={14} weight="bold" /> {upiSaving ? "Saving…" : "Save UPI"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="heading text-lg font-semibold">Edit policy</h2>
          <p className="text-sm text-slate-500">Choose which customer fields each role can edit directly. Unchecked fields will require Super Admin approval.</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={save} disabled={saving} data-testid="save-policy-btn">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save policy"}
          </Button>
        )}
      </div>

      <Card className="p-4 mb-4 flex items-start gap-3 bg-sky-50/40 border-sky-200">
        <Info size={18} className="text-sky-700 shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700">
          Super Admin can always edit any field directly. This table controls which of the other three roles can bypass approval for each field.
        </div>
      </Card>

      {msg && <div className="mb-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="policy-saved-msg">{msg}</div>}
      {err && <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{String(err)}</div>}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Field</th>
                {roles.map((r) => (
                  <th key={r} className="text-center px-4 py-3 font-semibold">{ROLE_LABEL[r] || r}</th>
                ))}
                <th className="text-center px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => {
                const allowedRoles = policy[f] || [];
                return (
                  <tr key={f} className="border-t border-slate-100" data-testid={`policy-row-${f}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{FIELD_LABEL[f] || f}</td>
                    {roles.map((r) => {
                      const on = allowedRoles.includes(r);
                      return (
                        <td key={r} className="px-4 py-3 text-center">
                          <button
                            type="button"
                            disabled={!isSuperAdmin}
                            onClick={() => toggle(f, r)}
                            data-testid={`policy-toggle-${f}-${r}`}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:cursor-not-allowed ${
                              on ? "bg-emerald-600" : "bg-slate-200"
                            } ${!isSuperAdmin ? "opacity-60" : ""}`}
                            aria-checked={on}
                            role="switch"
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                on ? "translate-x-5" : "translate-x-0.5"
                              }`}
                            />
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-center">
                      {allowedRoles.length === 0 ? (
                        <Badge tone="warning">Approval only</Badge>
                      ) : (
                        <Badge tone="success">Direct: {allowedRoles.length}</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk("");
    if (form.new_password.length < 8) return setErr("New password must be at least 8 characters.");
    if (form.new_password !== form.confirm) return setErr("New password and confirmation do not match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setOk("Password changed successfully. Use the new one at next sign-in.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map((x) => x.msg || x).join(", ") : (d || "Failed to change password."));
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 mb-6" data-testid="change-password-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
          <Bank size={20} weight="fill" />
        </div>
        <div className="flex-1">
          <div className="heading font-semibold">Change your password</div>
          <div className="text-xs text-slate-500 mb-3">
            The Super Admin should change the default password immediately after first login.
            Minimum 8 characters — use a strong, unique password.
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Current password" type="password" required
              testId="pw-current"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
            <Input label="New password" type="password" required
              testId="pw-new"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
            <Input label="Confirm new password" type="password" required
              testId="pw-confirm"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
            <div className="md:col-span-3 flex items-center gap-3">
              <Button type="submit" disabled={busy} data-testid="pw-submit">
                {busy ? "Saving…" : "Change password"}
              </Button>
              {err && <div className="text-sm text-rose-600" data-testid="pw-err">{err}</div>}
              {ok && <div className="text-sm text-emerald-700" data-testid="pw-ok">{ok}</div>}
            </div>
          </form>
        </div>
      </div>
    </Card>
  );
}
